import {
  Text,
  IconPlus,
  IndigoIndigo60,
  RadioButtonGroup,
  useNotifications,
  Button,
  Primary13SemiBold,
  Primary14SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import { CreditGrantTable } from './tables/CreditGrantTable'
import { CustomerCreditGrantTable } from './tables/CustomerCreditGrantTable'
import { CreditNoteTable } from './tables/CreditNoteTable'
import { InvoiceTable } from './tables/InvoiceTable'
import exhaustiveGuard from 'lib/exhaustiveGuard/exhaustiveGuard'
import { useCallback, useState } from 'react'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { CreateCreditGrantDrawer } from 'Customer/drawers/CreateCreditGrant/CreateCreditGrantDrawer'
import { CreateCustomerCreditGrantDrawer } from 'Customer/drawers/CreateCustomerCreditGrant/CreateCustomerCreditGrantDrawer'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useCreateInvoiceWithCustomerId } from 'Invoices/index'
import { Protected, ProtectedComponent } from 'lib/rbac'
import { CreateCreditNoteModal } from 'CreditNotesList/index'

type ActiveBillingTable = 'invoices' | 'credit-notes' | 'credit-grants'

export const BillingTables = () => {
  const { customer } = useCustomerContext()
  const [activeTable, setActiveTable] = useState<ActiveBillingTable>('invoices')
  const [showCreateCreditNoteModal, setShowCreateCreditNoteModal] =
    useState(false)
  const [showCreateCustomerCreditGrant, setShowCreateCustomerCreditGrant] =
    useState(false)
  const { useCustomerCreditGrantsV2 } = useFeatureFlags()
  const isArchived = Boolean(customer.archivedAt)
  const createInvoiceWithCustomerId = useCreateInvoiceWithCustomerId()
  const { displayPromiseNotification } = useNotifications()

  const handleCreateInvoice = useCallback(async () => {
    await displayPromiseNotification(createInvoiceWithCustomerId(customer.id), {
      loading: 'Creating invoice for customer',
      success: 'Invoice for customer created',
      error: 'Failed to create invoice'
    })
  }, [customer, displayPromiseNotification, createInvoiceWithCustomerId])

  const renderCreateButton = useCallback(() => {
    switch (activeTable) {
      case 'invoices':
        return (
          <ProtectedComponent permission="org.invoice:create">
            <Button
              {...Primary13SemiBold}
              variant="ghost"
              leadingIcon={
                <IconPlus color={isArchived ? undefined : IndigoIndigo60} />
              }
              disabled={isArchived}
              onClick={handleCreateInvoice}
            >
              Create invoice
            </Button>
          </ProtectedComponent>
        )
      case 'credit-notes':
        return (
          <ProtectedComponent permission="org.creditNote:create">
            <Button
              {...Primary13SemiBold}
              variant="ghost"
              leadingIcon={
                <IconPlus color={isArchived ? undefined : IndigoIndigo60} />
              }
              disabled={isArchived}
              onClick={() => setShowCreateCreditNoteModal(true)}
            >
              Create credit note
            </Button>
          </ProtectedComponent>
        )
      case 'credit-grants':
        return (
          <Protected permission="org.creditNote:create">
            {isAllowed => {
              if (!isAllowed) {
                return null
              }

              const createCreditGrantButton = (onClick?: () => void) => (
                <Button
                  data-testid={`${customer.id}.credit-grants.create`}
                  {...Primary13SemiBold}
                  variant="ghost"
                  leadingIcon={
                    <IconPlus color={isArchived ? undefined : IndigoIndigo60} />
                  }
                  disabled={isArchived}
                  onClick={onClick}
                >
                  Create credit grant
                </Button>
              )

              if (useCustomerCreditGrantsV2) {
                return createCreditGrantButton(() =>
                  setShowCreateCustomerCreditGrant(true)
                )
              }

              return (
                <CreateCreditGrantDrawer
                  customerId={customer.id}
                  customerCountry={customer.address?.country}
                  trigger={createCreditGrantButton()}
                />
              )
            }}
          </Protected>
        )
      default:
        return exhaustiveGuard(activeTable)
    }
  }, [
    activeTable,
    customer,
    isArchived,
    handleCreateInvoice,
    useCustomerCreditGrantsV2
  ])

  const renderTable = useCallback(() => {
    switch (activeTable) {
      case 'invoices':
        return <InvoiceTable />
      case 'credit-notes':
        return <CreditNoteTable />
      case 'credit-grants':
        return useCustomerCreditGrantsV2 ? (
          <CustomerCreditGrantTable />
        ) : (
          <CreditGrantTable />
        )
      default:
        return exhaustiveGuard(activeTable)
    }
  }, [activeTable, useCustomerCreditGrantsV2])

  return (
    <Box p={4}>
      <Text {...Primary14SemiBold}>Billing</Text>
      <Flex justifyContent="space-between" alignItems="center" mt={4}>
        <RadioButtonGroup
          data-testid={`${customer.id}`}
          onChange={value => setActiveTable(value as ActiveBillingTable)}
          initialValue={activeTable}
          options={[
            { value: 'invoices', label: 'Invoices' },
            { value: 'credit-notes', label: 'Credit notes' },
            { value: 'credit-grants', label: 'Credit grants' }
          ]}
        />
        {renderCreateButton()}
      </Flex>
      {renderTable()}
      {showCreateCreditNoteModal && (
        <CreateCreditNoteModal
          customer={customer}
          onClose={() => setShowCreateCreditNoteModal(false)}
        />
      )}
      {showCreateCustomerCreditGrant && (
        <CreateCustomerCreditGrantDrawer
          customerId={customer.id}
          onClose={() => setShowCreateCustomerCreditGrant(false)}
        />
      )}
    </Box>
  )
}
